import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import '../styles/dashboard.css';
import HomeIcon from '../components/icons/HomeIcon';
import AddIcon from '../components/icons/AddIcon';
import UserGuideIcon from '../components/icons/UserGuideIcon';
import ExpandSideNavIcon from '../components/icons/ExpandSideNavIcon';
import CollapseSidenavIcon from '../components/icons/CollapseSidenavIcon';
import DashboardHomeView from '../components/views/dashboard/DashboardHomeView';
import NewDashboardView from '../components/views/dashboard/NewDashboardView';
import DashboardWorkspaceView from '../components/views/dashboard/DashboardWorkspaceView';
import SpinningLoader from '../components/ui/SpinningLoader';
import Toast from '../components/ui/Toast';
import UserGuideOverlay from '../components/ui/UserGuideOverlay';
import { Project } from '../types/project';
import { projectsAPI, CreateProjectRequest } from '../services/projectsApi';
import DashboardProjectsView from '../components/views/dashboard/DashboardProjectsView';
import RenameProjectDialog from '../components/modals/RenameProjectDialog';
import ProjectsIcon from '../components/icons/ProjectsIcon';
import DashboardDocumentsView from '../components/views/dashboard/DashboardDocumentsView';
import DashboardMeetingsView from '../components/views/dashboard/DashboardMeetingsView';
import { DocumentsIcon } from '../components/icons/DocumentsIcon';
import { MeetingsIcon } from '../components/icons/MeetingsIcon';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [selectedProject, setSelectedProject] = useState<Project | undefined>();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectError, setProjectError] = useState<string | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('error');
  const [lastCreatedProject, setLastCreatedProject] = useState<Project | null>(null);
  const [triggerDeleteProject, setTriggerDeleteProject] = useState<Project | null>(null);
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [isSidenavExpanded, setIsSidenavExpanded] = useState(false);
  const [currentProjectName, setCurrentProjectName] = useState('');
  const [isAgentMode, setIsAgentMode] = useState<boolean>(true); // Agent mode state shared across views

  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'error') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  // Global keyboard shortcut for creating new project (⌘+N or Ctrl+N)
  // TODO: Re-enable once browser shortcut override issue is resolved
  // useEffect(() => {
  //   const handleKeyDown = (event: KeyboardEvent) => {
  //     const isCmdOrCtrl = event.metaKey || event.ctrlKey;
  //     const isN = event.key === 'n' || event.key === 'N' || event.keyCode === 78;
  //
  //     if (isCmdOrCtrl && isN) {
  //       event.preventDefault();
  //       event.stopPropagation();
  //       event.stopImmediatePropagation();
  //       handleCreateClick();
  //       return false;
  //     }
  //   };
  //
  //   window.addEventListener('keydown', handleKeyDown, { capture: true, passive: false });
  //   return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  // }, [location.pathname]);

  const hideToast = () => {
    setToastVisible(false);
  };

  // Load projects from API
  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const projectsList = await projectsAPI.listProjects({ status: 'active', limit: 100 });
      setProjects(projectsList);
    } catch (err) {
      console.error('Error loading projects:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load projects';
      setError(errorMessage);
      showToast('Something went wrong. Failed to fetch projects', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load projects when component mounts
  useEffect(() => {
    loadProjects();
  }, []);

  // Reload projects when navigating to pages with ProjectGrid
  useEffect(() => {
    if (
      location.pathname === '/dashboard' ||
      location.pathname === '/' ||
      location.pathname === '/dashboard/projects' ||
      location.pathname === '/dashboard/create'
    ) {
      loadProjects();
    }
  }, [location.pathname]);

  // Effect to handle URL changes and load project data
  useEffect(() => {
    console.log(id);
    if (id) {
      const loadProject = async () => {
        setProjectLoading(true);
        setProjectError(null);
        setSelectedProject(undefined); // Clear previous project

        try {
          const project = await projectsAPI.getProject(id);
          setSelectedProject(project);
        } catch (err) {
          console.error('Error loading project:', err);

          // Always redirect to dashboard home on project loading error
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000); // Small delay to let user see the toast

          setProjectError('Failed to load project');
        } finally {
          setProjectLoading(false);
        }
      };

      loadProject();
    } else {
      setSelectedProject(undefined);
      setProjectError(null);
      setProjectLoading(false);
    }
  }, [id, navigate]); // Removed projects dependency as we no longer use it as fallback

  // Sync isAgentMode with project's mode when project is loaded
  // NOTE: isAgentMode is NOT in deps to avoid overriding user's manual mode changes
  useEffect(() => {
    const currentProject = selectedProject || lastCreatedProject;
    if (currentProject?.mode) {
      const shouldBeAgentMode = currentProject.mode === 'research';
      console.log('[Dashboard] Project mode check:', {
        projectId: currentProject.project_id,
        projectMode: currentProject.mode,
        currentIsAgentMode: isAgentMode,
        shouldBeAgentMode,
        needsSync: isAgentMode !== shouldBeAgentMode
      });
      if (isAgentMode !== shouldBeAgentMode) {
        console.log('[Dashboard] Syncing isAgentMode with project.mode:', currentProject.mode, '→ isAgentMode:', shouldBeAgentMode);
        setIsAgentMode(shouldBeAgentMode);
      }
    }
  }, [selectedProject, lastCreatedProject]);

  const handleHomeClick = () => {
    navigate('/dashboard');
  };

  const handleCreateClick = () => {
    // Don't open dialog if already on create page
    const path = location.pathname;
    if (path === '/dashboard/create') {
      return;
    }
    setNameDialogOpen(true);

    // navigate('/dashboard/create', {
    //   state: { projectName: '' },
    //   replace: true  // Replace current history entry to prevent back button issues
    // });
  };

  const handleNameCancel = () => {
    setNameDialogOpen(false);
  };

  const handleNameConfirm = async (name: string) => {
    setNameDialogOpen(false);
    setCurrentProjectName(name);

    // Create the project
    await handleSaveProject({ name });

    navigate('/dashboard/create', {
      replace: true  // Replace current history entry to prevent back button issues
    });
  };

  const handleRenameProjectName = (newName: string) => {
    setCurrentProjectName(newName);
  };

  const handleUserGuideClick = () => {
    setShowUserGuide(true);
  };

  const handleViewProject = (project: Project) => {
    if (project.is_draft) {
      // For draft projects, navigate to create page and set the project
      setLastCreatedProject(project);
      setCurrentProjectName(project.name);
      navigate('/dashboard/create');
    } else {
      // For non-draft projects, navigate to project view
      navigate(`/dashboard/project/${project.project_id}`);
    }
  };

  const handleCreateProject = () => {
    // Don't open dialog if already on create page
    const path = location.pathname;
    if (path === '/dashboard/create') {
      return;
    }
    setNameDialogOpen(true);
    // navigate('/dashboard/create', {
    //   state: { projectName: '' },
    //   replace: true  // Replace current history entry to prevent back button issues
    // });
  };

  const handleBackToHome = () => {
    navigate('/dashboard');
  };

  const handleSaveProject = async (projectData: Partial<Project>) => {
    try {
      const createRequest: CreateProjectRequest = {
        name: projectData.name || 'New Project',
        description: projectData.description || '',
        initial_query: projectData.initial_query || '',
        metadata: projectData.metadata || {},
        mode: isAgentMode ? 'research' : 'chat'
      };

      console.log('[Dashboard] Creating project with mode:', createRequest.mode, 'isAgentMode:', isAgentMode);
      const newProject = await projectsAPI.createProject(createRequest);

      // Store the created project for undo functionality
      setLastCreatedProject(newProject);

      // Refresh projects list
      const updatedProjects = await projectsAPI.listProjects({ status: 'active', limit: 100 });
      setProjects(updatedProjects);

      // Show success toast
      showToast(`"${newProject.name}" has been successfully created`, 'success');

      // Collapse the sidenav when navigating after project creation
      setIsSidenavExpanded(false);

    } catch (err) {
      console.error('Error creating project:', err);
      showToast('Something went wrong. Failed to create new project', 'error');
      // Go back to home on error
      handleBackToHome();
    }
  };

  const handleCreateProjectWithQuestion = async (name: string, question: string, attachedFiles: File[]) => {
    try {
      // Create project with initial question
      const createRequest: CreateProjectRequest = {
        name: name ? name : `${question.substring(0, 50)}${question.length > 50 ? '...' : ''}`,
        description: question,
        initial_query: question,
        metadata: {
          attachedFiles: attachedFiles.map(f => ({ name: f.name, size: f.size, type: f.type }))
        },
        mode: isAgentMode ? 'research' : 'chat'
      };

      console.log('[Dashboard] Creating project with question, mode:', createRequest.mode, 'isAgentMode:', isAgentMode);
      const newProject = await projectsAPI.createProject(createRequest);

      // Store the created project for undo functionality
      setLastCreatedProject(newProject);

      // Refresh projects list
      const updatedProjects = await projectsAPI.listProjects({ status: 'active', limit: 100 });
      setProjects(updatedProjects);

      // Collapse the sidenav when navigating to project
      setIsSidenavExpanded(false);

      // Navigate to the new project with flag to auto-submit query only after successful creation
      navigate(`/dashboard/project/${newProject.project_id}`, {
        state: {
          initialQuestion: question,
          initialAttachments: attachedFiles,
          autoSubmitQuery: true // Flag to indicate automatic query submission
        },
        replace: true // Replace the create page in history to prevent back navigation
      });
    } catch (err) {
      console.error('Error creating project with question:', err);
      showToast('Something went wrong. Failed to create new project', 'error');
      handleBackToHome();
      throw err; // Re-throw so the calling component can handle the error
    }
  };

  const handleRenameProject = async (project: Project, newName: string) => {
    try {
      await projectsAPI.updateProject(project.project_id, { name: newName });
      // Refresh projects list
      const updatedProjects = await projectsAPI.listProjects({ status: 'active', limit: 100 });
      setProjects(updatedProjects);
    } catch (err) {
      console.error('Error renaming project:', err);
      throw err; // Re-throw so the component can handle the error state
    }
  };

  const handleDeleteProject = async (project: Project) => {
    try {
      await projectsAPI.deleteProject(project.project_id);
      // Refresh projects list
      const updatedProjects = await projectsAPI.listProjects({ status: 'active', limit: 100 });
      setProjects(updatedProjects);

      // Clear the last created project if it was deleted
      if (lastCreatedProject && lastCreatedProject.project_id === project.project_id) {
        setLastCreatedProject(null);
      }
    } catch (err) {
      console.error('Error deleting project:', err);
      throw err; // Re-throw so the modal can handle the error state
    }
  };

  const handleUndoCreateProject = () => {
    if (lastCreatedProject) {
      setToastVisible(false);
      // Navigate to home first if not already there
      if (location.pathname !== '/dashboard') {
        navigate('/dashboard');
      }
      // Trigger the delete modal for the last created project
      setTriggerDeleteProject(lastCreatedProject);
    }
  };

  const handleAgentModeChange = async (newMode: boolean) => {
    console.log('[Dashboard] handleAgentModeChange called with:', newMode);

    // Update local state immediately for responsive UI
    setIsAgentMode(newMode);

    // Persist mode to project if we have one
    const currentProject = lastCreatedProject || selectedProject;
    if (currentProject) {
      const modeString = newMode ? 'research' : 'chat';
      console.log('[Dashboard] Persisting mode to project:', currentProject.project_id, '→', modeString);
      try {
        await projectsAPI.updateProject(currentProject.project_id, { mode: modeString });

        // Update local project state to reflect the change
        if (lastCreatedProject && lastCreatedProject.project_id === currentProject.project_id) {
          setLastCreatedProject({ ...lastCreatedProject, mode: modeString });
        }
        if (selectedProject && selectedProject.project_id === currentProject.project_id) {
          setSelectedProject({ ...selectedProject, mode: modeString });
        }
      } catch (err) {
        console.error('Error updating project mode:', err);
        // Don't show error toast - mode change should work locally even if API fails
      }
    }
  };

  const renderCurrentView = () => {
    const path = location.pathname;

    if (loading) {
      return (
        <div className="dashboard-content">
          <div className="dashboard-loading">
            <SpinningLoader size="large" text="Loading dashboard..." />
          </div>
        </div>
      );
    }

    // Remove error state - let the home page load with empty projects

    if (path === '/dashboard/projects') {
      return (

        <DashboardProjectsView
          projects={projects}
          onCreateProject={handleCreateProject}
          onViewProject={handleViewProject}
          onRenameProject={handleRenameProject}
          onDeleteProject={handleDeleteProject}
          triggerDeleteProject={triggerDeleteProject}
          onDeleteProjectTriggered={() => setTriggerDeleteProject(null)}
        />
      );
    } else if (path.startsWith('/dashboard/project/')) {
      // Handle project loading states
      if (projectLoading) {
        return (
          <div className="dashboard-content">
            <div className="dashboard-loading">
              <SpinningLoader size="large" text="Loading project..." />
            </div>
          </div>
        );
      }

      if (!selectedProject) {
        // This case should rarely be hit since errors now redirect automatically
        return (
          <div className="dashboard-content">
            <div className="dashboard-loading">
              <SpinningLoader size="large" text="Redirecting..." />
            </div>
          </div>
        );
      }

      return (
        <DashboardWorkspaceView
          project={selectedProject || lastCreatedProject}
          isAgentMode={isAgentMode}
          onAgentModeChange={handleAgentModeChange}
        />
      );
    } else if (path === '/dashboard/home') {
      return (
        <DashboardHomeView
          projects={projects}
          onCreateProject={handleCreateProject}
          onViewProject={handleViewProject}
          onRenameProject={handleRenameProject}
          onDeleteProject={handleDeleteProject}
          triggerDeleteProject={triggerDeleteProject}
          onDeleteProjectTriggered={() => setTriggerDeleteProject(null)}
        />
      );
    } else if (path === '/dashboard/documents') {
      return <DashboardDocumentsView />;
    } else if (path === '/dashboard/meetings') {
      return <DashboardMeetingsView />;
    } else {
      return (
        <NewDashboardView
          isCreating={path === '/dashboard/create'}
          projects={path === '/dashboard/create' ? [] : projects}
          project={path === '/dashboard/create' ? (lastCreatedProject || undefined) : undefined}
          title={path === '/dashboard/create' ? 'Create New Project' : 'Home'}
          projectName={currentProjectName}
          onBack={handleBackToHome}
          onSave={handleSaveProject}
          onCreateWithQuestion={handleCreateProjectWithQuestion}
          onViewProject={handleViewProject}
          onRenameProject={handleRenameProject}
          onDeleteProject={handleDeleteProject}
          onRenameProjectName={handleRenameProjectName}
          onResetProject={() => {
            setLastCreatedProject(null);
            setCurrentProjectName('');
          }}
          isAgentMode={isAgentMode}
          onAgentModeChange={handleAgentModeChange}
        />
      );
    }
  };

  return (
    <>
      <div className="aspire-dashboard">
        {/* Sidebar */}
        <aside className={`sidebar ${isSidenavExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="sidebar-header">
            <div className="logo-container" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
              <img
                src="/images/columbus-small.png"
                alt="Columbus Logo"
                className="logo-image"
              />
              {isSidenavExpanded && (
                <div>
                <div className="logo-text">Board Navigator</div>
                </div>
              )}
            </div>
          </div>

          <nav className="nav-menu">
            <button
              className={`nav-item ${location.pathname === '/dashboard' || location.pathname === '/' ? 'active' : ''}`}
              onClick={handleHomeClick}
              title="Home"
            >
              <HomeIcon className="nav-icon" size={24} />
              {isSidenavExpanded && <span className="nav-label">Home</span>}
            </button>
            <button
              className={`projects nav-item ${location.pathname === '/dashboard/projects' || location.pathname.startsWith('/dashboard/project/') ? 'active' : ''}`}
              onClick={() => navigate('/dashboard/projects')}
              title="All Projects"
            >
              <ProjectsIcon className="nav-icon projects" size={10} />
              {isSidenavExpanded && <span className="nav-label">All Projects</span>}
            </button>
            <button
              className={`nav-item ${location.pathname === '/dashboard/create' ? 'active' : ''}`}
              onClick={handleCreateClick}
              title="Add New Project"
            >
              <AddIcon className="nav-icon" size={24} />
              {isSidenavExpanded && <span className="nav-label">Create New Project</span>}
            </button>
                        <button
              className={`nav-item ${location.pathname === '/dashboard/meetings' ? 'active' : ''}`}
              onClick={() => navigate('/dashboard/meetings')}
              title="Meetings"
            >
              <MeetingsIcon className="nav-icon" color="currentColor" />
              {isSidenavExpanded && <span className="nav-label">Meetings</span>}
            </button>
            <button
              className={`nav-item ${location.pathname === '/dashboard/documents' ? 'active' : ''}`}
              onClick={() => navigate('/dashboard/documents')}
              title="Documents"
            >
              <DocumentsIcon className="nav-icon" color="currentColor" />
              {isSidenavExpanded && <span className="nav-label">Documents</span>}
            </button>

          </nav>

          <div className="sidebar-footer">
            {isSidenavExpanded && (<p className="partner-title">Powered by</p>)}
            <div className={`partner-logos ${isSidenavExpanded ? 'expanded' : 'collapsed'}`}>
              <div className="partner-logo-wrapper">
                <img src={isSidenavExpanded ? "/images/columbus.svg" : "/images/columbus-small.png"} alt="Columbus logo" className="partner-logo" />
              </div>
            </div>
          </div>

          {/* Expand button at bottom */}
          {!isSidenavExpanded && (
            <>
            <div className="expand-container">
              <button
                className="expand-btn"
                onClick={() => setIsSidenavExpanded(true)}
                title="Expand Sidebar"
                >
                <ExpandSideNavIcon size={16} />
              </button>
            </div>
                </>
          )}
          {isSidenavExpanded && (
            <>
            <div style={{ borderTop: '2px solid var(--stroke-sperator, #F5F5F5)', paddingTop: '8px', marginLeft: '-20px', marginRight: '-20px'}} />
            <button
              className="collapse-btn"
              onClick={() => setIsSidenavExpanded(false)}
              title="Collapse Sidebar"
            >
              <CollapseSidenavIcon size={20} /> Collapse Sidebar
            </button>
                        </>
          )}
        </aside>

        {/* Main Content */}
        {renderCurrentView()}

        {/* Toast for error messages */}
        <Toast
          message={toastMessage}
          type={toastType}
          isVisible={toastVisible}
          onClose={hideToast}
          duration={4000}
          actionLabel={toastType === 'success' && lastCreatedProject ? 'Undo' : undefined}
          onAction={toastType === 'success' && lastCreatedProject ? handleUndoCreateProject : undefined}
        />

        {/* User Guide Overlay */}
        <UserGuideOverlay
          isOpen={showUserGuide}
          onClose={() => setShowUserGuide(false)}
        />
      </div>

      {/* Create Project Name Dialog */}
      <RenameProjectDialog
        isOpen={nameDialogOpen}
        onClose={handleNameCancel}
        onRename={handleNameConfirm}
        dialogTitle='Project Name'
        isCreate={true}
        projectName={''}
      />
    </>
  );
};

export default Dashboard;